import { useTranslation } from '@/lib/i18n';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { PhotoPlaceholder } from '../primitives';
import type { Milestone } from './milestones-data';

/**
 * How long a slide takes to zoom into its new size, and on what curve.
 *
 * An ease-in-out, but a SHALLOW one, and this is the whole trick: `0.25` for the first handle rather
 * than the `0.42` a textbook ease-in-out uses. A deep ease-in starts the zoom from rest, and a zoom that
 * starts from rest reads as lag — the thing that triggered it (the scroll, the click) has already
 * happened, so any pause at the front is felt as the section being slow to answer. At 0.25 the card is
 * already moving in the first frames while the curve still rounds off BOTH ends, which is what makes it
 * read as eased rather than as a ramp.
 *
 * 500ms, matched to the travel transition below rather than chosen for the zoom alone. The two describe
 * the same movement — the card crossing the canvas and the card changing size — so they have to start
 * together and, more importantly, ARRIVE together: at the 700ms this had before, the size was still
 * settling after the slide had stopped, which is exactly the delay that gets read as jank.
 */
const SLIDE = 'duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)]';

/** The artwork, in the three shapes the hand-off uses. See {@link Milestone} for what each one means. */
function MilestoneArtwork(props: { image: NonNullable<Milestone['image']> }) {
	const { language } = useTranslation();
	const image = props.image;

	if (image.kind === 'illustration') {
		// Centred, then lifted 9px of the card's 318 (hence 47.2% rather than 50%). The height is left to
		// the SVG's own ratio, which is what makes it overhang the top edge.
		return (
			<img
				src={image.src}
				alt={image.alt}
				style={{ width: image.width }}
				className="pointer-events-none absolute top-[47.2%] left-1/2 h-auto -translate-x-1/2 -translate-y-1/2"
			/>
		);
	}

	if (image.kind === 'screen') {
		// Anchored to the CORNER, not centred: the capture is meant to run off the card's right and
		// bottom edges, which the card's own `overflow-hidden` then crops.
		return (
			<img
				src={image.src}
				alt={image.alt}
				style={{ width: image.width }}
				className="pointer-events-none absolute right-0 bottom-0 h-auto"
			/>
		);
	}

	return (
		<Image
			src={typeof image.src === 'string' ? image.src : (image.src[language] ?? image.src.en)}
			alt={image.alt}
			height={318}
			aspectRatio={442 / 318}
			objectFit="cover"
			className="pointer-events-none size-full object-cover!"
		/>
	);
}

/**
 * One milestone: its card and its caption, as one rigid unit that travels and zooms together.
 *
 * TWO notions of "active", because a year the design draws as several cards is ONE slide at `xl` and
 * one swipe per card below it. `isFocused` is the slide the track is actually on — the only one at full
 * size on mobile; `inFocusedYear` is every card of that slide's year, which is what makes both 2025
 * cards read as present at `xl`. Base classes take the first, the `xl:` layer the second.
 *
 * `yearOffset` rather than a slide offset for the inward pull, for the same reason: the two cards of one
 * year are not neighbours of each other, so neither may pull towards the other.
 */
export function MilestoneCard(props: {
	milestone: Milestone;
	isFocused: boolean;
	inFocusedYear: boolean;
	yearOffset: number;
	onSelect: () => void;
}) {
	const image = props.milestone.image;

	return (
		/* The TRAVEL, on its own element and its own curve, because it answers to something the zoom does
		   not: the browser's smooth scroll of the track underneath it, which is over in about 350ms. Left
		   on the zoom's 700ms the pull was still unwinding after the slide had arrived and the card slid
		   34px past the centre and drifted back — and that is a trap that reopens every time the zoom's
		   curve is retuned, which is reason enough for the two to be separate. */
		<div
			className={cn(
				'w-full transition-[translate] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] xl:w-[442px]',
				// The inward pull the export draws — a neighbour's card 774.5px from the active one rather
				// than the full 843px step. A translate rather than a lopsided transform-origin because it
				// can animate THROUGH zero as a slide passes the centre, where an origin that flipped to the
				// other edge would move the card 137px in the single frame it flipped.
				props.yearOffset < 0 && 'xl:translate-x-[68.5px]',
				props.yearOffset > 0 && 'xl:-translate-x-[68.5px]',
			)}
		>
			{/* Clicking a neighbour walks the timeline; the rail stays the primary control, which is why
			    this is not a button — it would double every year in the tab order. No click guard needed:
			    a native scroll is not a click, so a swipe that ends here does not also jump. */}
			<div
				className={cn(
					// `scale`, NOT `transform`: Tailwind writes the individual property, so a transition list
					// naming `transform` covers nothing at all and the slide snapped between its two sizes.
					'flex w-full flex-col gap-[9px] transition-[opacity,scale] xl:origin-[50%_159px]',
					SLIDE,
					props.isFocused ? 'opacity-100' : 'cursor-pointer opacity-25',
					// ONE scale for the whole slide, on the shared parent rather than on the card and the
					// caption separately. Two scales about two different origins landed in the right places
					// but got there along different paths — the card's bottom edge swept down out of its own
					// centre while the caption's top stayed pinned — so the pair visibly came apart
					// mid-transition. Here the caption rides at a fixed fraction of the card's height
					// throughout. The origin is the CARD'S CENTRE, 159px being half of its 318.
					!props.inFocusedYear && 'xl:scale-[0.69]',
					!props.isFocused && props.inFocusedYear && 'xl:cursor-default xl:opacity-100',
				)}
				onClick={props.isFocused ? undefined : props.onSelect}
			>
				<div
					className={cn(
						// The card's size is pure LAYOUT — 442x318 on every slide, the same box throughout — and the
						// shrinking is entirely the slide's own scale above. Nothing here animates, which is exactly
						// why the card cannot drift away from its caption.
						'relative aspect-[442/318] w-full shrink-0 overflow-hidden rounded-3xl',
						// #FFEDDF is the hand-off's own plate colour behind the photos and drawings, the same literal
						// the hero's app card uses — see the note there. A `screen` inverts it: the design puts the
						// product capture on a dark plate so the UI itself reads.
						image?.kind === 'screen' ? 'bg-ink' : 'bg-[#FFEDDF]',
						'xl:aspect-auto xl:h-[318px] xl:w-[442px]',
					)}
				>
					{/* No `label` on the placeholder: the primitive's own default covers it, and a msgid for
					    scaffolding that gets deleted the moment the photographs arrive would cost ten translations. */}
					{image ? <MilestoneArtwork image={image} /> : <PhotoPlaceholder className="size-full" />}
				</div>
				{/* No scale of its own: the type is 20px on every slide and the slide's own transform is what
				    sizes it, which is what keeps it welded to its card. */}
				{props.milestone.caption && (
					<p className="text-ink w-full text-base leading-[1.5] tracking-[-0.02em] xl:text-xl">
						{props.milestone.caption}
					</p>
				)}
			</div>
		</div>
	);
}
